import { z } from "zod";
import { ACTIVITIES, NOTE_METHODS } from "./enums.ts";
export const NOTE_COLORS = ["white", "yellow", "pink", "blue", "green", "lavender"] as const;
export const NOTE_PAPERS = ["blank", "lined", "grid", "dots"] as const;
export const NOTE_FONTS = ["sans", "serif", "mono"] as const;
export const NOTE_SECTIONS: Record<(typeof NOTE_METHODS)[number], string[]> = {
  quick: ["Nội dung"],
  plain: ["Nội dung"],
  cornell: ["Gợi ý / câu hỏi", "Ghi chép", "Tóm tắt"],
  box: ["Từ vựng", "Cấu trúc", "Ví dụ", "Lưu ý"],
  map: ["Chủ đề", "Ý chính", "Liên hệ", "Ví dụ"],
  mind: ["Trung tâm", "Nhánh 1", "Nhánh 2", "Nhánh 3"],
};
/**
 * Bản đồ / sơ đồ tư duy cho thêm nhánh thoải mái; giới hạn này chỉ để chặn dữ liệu bất thường
 * (giới hạn kích thước request vẫn áp dụng).
 */
export const NOTE_MAX_BRANCHES = 60;
export const NoteDoc = z.strictObject({
  html: z.string().max(50000).default(""),
  /** Phần 0 là chủ đề/ý trung tâm, phần i ≥ 1 là nội dung nhánh i (hoặc ô của Cornell, hộp). */
  sections: z
    .array(z.string().max(30000))
    .max(NOTE_MAX_BRANCHES + 1)
    .default([]),
  /** Tên nhánh do người học đặt, cùng chỉ số với `sections`; rỗng thì dùng tên mặc định. */
  labels: z
    .array(z.string().trim().max(200))
    .max(NOTE_MAX_BRANCHES + 1)
    .default([]),
  assets: z.array(z.uuid()).max(20).default([]),
  stickers: z
    .array(
      z.strictObject({
        text: z.enum(["⭐", "🐟", "💡", "📌", "✅", "⚠️"]),
        x: z.number().min(0).max(95),
        y: z.number().min(0).max(95),
      }),
    )
    .max(30)
    .default([]),
});
export type NoteDoc = z.infer<typeof NoteDoc>;
export const NoteContext = z.strictObject({
  path: z
    .string()
    .max(300)
    .startsWith("/")
    .refine(
      (p) =>
        !p.startsWith("//") &&
        !p.includes("\\") &&
        Array.from(p).every((c) => c.charCodeAt(0) > 32),
      "Chỉ nhận đường dẫn nội bộ.",
    ),
  label: z.string().max(150),
  question: z.string().max(150).optional(),
  activity: z.enum(ACTIVITIES).optional(),
});
export const NoteInput = z.strictObject({
  rev: z.int().min(0),
  method: z.enum(NOTE_METHODS),
  title: z.string().trim().max(200),
  doc: NoteDoc,
  paper: z.enum(NOTE_PAPERS).default("lined"),
  color: z.enum(NOTE_COLORS).default("white"),
  font: z.enum(NOTE_FONTS).default("sans"),
  tags: z.array(z.string().trim().min(1).max(40)).max(12).default([]),
  context: NoteContext.nullable().default(null),
  attemptId: z.uuid().nullable().default(null),
});
export type NoteInput = z.infer<typeof NoteInput>;
export const Note = z.object({
  ...NoteInput.shape,
  id: z.uuid(),
  rev: z.int().min(1),
  source: z.string(),
  sourceNoteId: z.uuid().nullable(),
  updatedAt: z.iso.datetime(),
  pinnedAt: z.iso.datetime().nullable().default(null),
});
export type Note = z.infer<typeof Note>;
export const NoteList = z.object({
  items: z.array(Note),
  total: z.int(),
  limit: z.int().nullable(),
  nextOffset: z.int().nullable(),
});
export const NoteReview = z.object({
  id: z.uuid(),
  note: Note,
  reps: z.int(),
  dueAt: z.iso.datetime(),
  lastReviewedAt: z.iso.datetime().nullable(),
});
export type NoteReview = z.infer<typeof NoteReview>;
export const NOTE_INTERVALS = [1, 3, 7, 14, 30] as const;
export const NoteAssetInput = z.strictObject({
  id: z.uuid(),
  noteId: z.uuid(),
  mime: z.enum(["image/png", "image/jpeg"]),
  bytes: z
    .int()
    .min(1)
    .max(3 * 1024 * 1024),
});
export const NoteAssetTicket = z.object({
  id: z.uuid(),
  url: z.url(),
  expiresAt: z.iso.datetime(),
  mime: z.string(),
});
export const NoteAssetView = z.object({
  id: z.uuid(),
  url: z.url(),
  expiresAt: z.iso.datetime(),
  width: z.int(),
  height: z.int(),
});
